// frontend/src/components/pages/home/PlansSection.jsx
import { useEffect, useState } from "react";
import { getAllPlanForUnAuth } from "../../../api/apiFunction/planServices";
import { createCheckoutSession } from "../../../api/apiFunction/paymentServices";

// This section shows pricing on the home page with left header and right plan card
const PlansSection = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await getAllPlanForUnAuth();
        if (res?.data && Array.isArray(res.data)) {
          setPlans(res.data);
        } else {
          setPlans([]); // Ensure plans is always an array
        }
      } catch (error) {
        setPlans([]); // Ensure plans is always an array on error
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  if (loading) {
    return (
      <div className="py-24 bg-bg-50">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-pulse text-fg-50">Loading plans...</div>
        </div>
      </div>
    );
  }

  // Ensure plans is always an array before filtering
  const plansArray = Array.isArray(plans) ? plans : [];

  // Separate individual and organization plans
  const individualPlans = plansArray.filter(
    (plan) => plan.planType === "individual"
  );
  const organizationPlans = plansArray.filter(
    (plan) => plan.planType === "organization"
  );

  const individualPlan =
    individualPlans.find((p) => (p?.name || "").toLowerCase() === "pro") ||
    individualPlans[0];

  // If no plans are available, show a message
  if (!loading && plansArray.length === 0) {
    return (
      <section className="py-24 bg-bg-50 relative z-0">
        <div className="container mx-auto px-4 text-center">
          <div className="text-fg-60">
            <h2 className="text-2xl font-bold mb-4">Plans Coming Soon</h2>
            <p>
              We're working on bringing you the best pricing plans. Please check
              back later.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Helper function to get features for a plan
  const getFeatures = (plan) =>
    [
      plan?.employeeLimit !== undefined && {
        label: "Employee Limit",
        value: plan.employeeLimit === -1 ? "Unlimited" : plan.employeeLimit,
        type: "general",
      },
      plan?.dailyLimits?.highEndLlmTokens !== undefined && {
        label: "High-End LLM Tokens",
        daily:
          plan.dailyLimits.highEndLlmTokens === -1
            ? "Unlimited"
            : plan.dailyLimits.highEndLlmTokens,
        monthly:
          plan.monthlyLimits?.highEndLlmTokens === -1
            ? "Unlimited"
            : plan.monthlyLimits?.highEndLlmTokens,
        type: "limit",
      },
      plan?.dailyLimits?.smallLlmTokens !== undefined && {
        label: "Small LLM Tokens",
        daily:
          plan.dailyLimits.smallLlmTokens === -1
            ? // If daily is unlimited, derive a practical daily number from monthly/30 when available
              plan.monthlyLimits?.smallLlmTokens &&
              plan.monthlyLimits.smallLlmTokens > 0
              ? Math.ceil(plan.monthlyLimits.smallLlmTokens / 30)
              : "Unlimited"
            : plan.dailyLimits.smallLlmTokens,
        monthly:
          plan.monthlyLimits?.smallLlmTokens === -1
            ? "Unlimited"
            : plan.monthlyLimits?.smallLlmTokens,
        type: "limit",
      },
      plan?.dailyLimits?.images !== undefined && {
        label: "Image Generations",
        daily:
          plan.dailyLimits.images === -1
            ? "Unlimited"
            : plan.dailyLimits.images,
        monthly:
          plan.monthlyLimits?.images === -1
            ? "Unlimited"
            : plan.monthlyLimits?.images,
        type: "limit",
      },
      plan?.dailyLimits?.webSearches !== undefined && {
        label: "Web Searches",
        daily:
          plan.dailyLimits.webSearches === -1
            ? "Unlimited"
            : plan.dailyLimits.webSearches,
        monthly:
          plan.monthlyLimits?.webSearches === -1
            ? "Unlimited"
            : plan.monthlyLimits?.webSearches,
        type: "limit",
      },
      plan?.dailyLimits?.youtube !== undefined && {
        label: "YouTube",
        daily:
          plan.dailyLimits.youtube === -1
            ? "Unlimited"
            : plan.dailyLimits.youtube,
        monthly:
          plan.monthlyLimits?.youtube === -1
            ? "Unlimited"
            : plan.monthlyLimits?.youtube,
        type: "limit",
      },
    ].filter(Boolean);

  // Check if user is already subscribed to a plan
  const userObj = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : null;
  const userPlanId = userObj?.plan?._id;

  const handleUpgrade = async (planId) => {
    const userObj = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null;
    if (!userObj) {
      window.location.href = "/register";
      return;
    }
    const userId = userObj?._id || userObj?.id || null;
    try {
      const res = await createCheckoutSession(planId, userId);
      if (res.data && res.data.checkoutUrl) {
        if (res.data.sessionId) {
          localStorage.setItem("thawani_session_id", res.data.sessionId);
        }
        window.location.href = res.data.checkoutUrl;
      } else {
        alert("Failed to initiate payment.");
      }
    } catch (err) {
      alert("Payment initiation failed.");
    }
  };

  // Component to render a single plan card
  const PlanCard = ({ plan, planType, blobColor }) => {
    const features = getFeatures(plan);
    const isCurrentPlan = userPlanId && plan?._id && userPlanId === plan._id;

    return (
      <div className="bg-bg-70 rounded-3xl p-8 border border-bd-50 relative overflow-hidden z-0">
        {/* Gradient Blob Effect */}
        <div
          className={`absolute -top-12 -left-12 w-60 h-60 ${blobColor} rounded-full blur-3xl opacity-20`}
        />

        {/* Content */}
        <div className="relative z-10">
          {/* Plan Type Badge */}
          <div className="mb-6">
            <span className="inline-block px-3 py-1 bg-bg-70 text-fg-60 text-xs font-medium rounded-full">
              {planType} Plan
            </span>
          </div>

          {/* Pricing - Left Aligned */}
          <div className="mb-6">
            <div className="mb-1">
              <span className="text-fg-60 text-xs">Start at</span>
            </div>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-4xl font-bold text-fg-40">
                ${plan?.baseCost || 7}
              </span>
              <span className="text-fg-60 text-base">/month</span>
            </div>

            {/* Plan Description */}
            <p className="text-fg-60 text-xs mb-6 leading-relaxed">
              {planType === "Individual"
                ? "Sign up for exclusive access to all our reports, tools."
                : "Sign up for exclusive access to all our reports, tools."}
            </p>

            {/* CTA Button */}
            {isCurrentPlan ? (
              <div className="bg-green-500/20 border border-green-500/30 rounded-full p-3 mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="text-green-600 font-medium text-sm">
                    Current Plan
                  </span>
                </div>
              </div>
            ) : (
              <button
                onClick={() => handleUpgrade(plan._id)}
                className="w-full bg-transparent border border-bd-50 text-fg-40 py-3 px-6 rounded-full text-sm font-normal hover:bg-bg-50 transition-all duration-200 mb-6"
              >
                Subscribe {planType.toLowerCase()} plan
              </button>
            )}
          </div>

          {/* Usage Limits */}
          <div>
            <h4 className="text-fg-60 text-base font-normal mb-4">
              Usage Limits
            </h4>

            {/* Table header */}
            <div className="grid grid-cols-3 gap-4 mb-3 pb-2 px-3 py-2 bg-bg-50 rounded-lg">
              <div className="text-fg-60 text-xs font-medium">Feature</div>
              <div className="text-fg-60 text-xs font-medium">Daily</div>
              <div className="text-fg-60 text-xs font-medium">Monthly</div>
            </div>

            {/* Feature rows */}
            <div className="space-y-3">
              {features
                .filter((f) => f.type === "limit")
                .map((feature, index) => (
                  <div
                    key={`${planType}-limit-${index}`}
                    className="grid grid-cols-3 gap-4 py-1"
                  >
                    <div className="text-fg-60 text-xs">{feature.label}</div>
                    <div className="text-fg-40 text-xs font-normal">
                      {feature.daily === "Unlimited"
                        ? "Unlimited"
                        : feature.daily === -1
                        ? "Unlimited"
                        : typeof feature.daily === "number"
                        ? feature.daily.toLocaleString()
                        : feature.daily || "N/A"}
                    </div>
                    <div className="text-fg-40 text-xs font-normal">
                      {feature.monthly === "Unlimited"
                        ? "Unlimited"
                        : feature.monthly === -1
                        ? "Unlimited"
                        : typeof feature.monthly === "number"
                        ? feature.monthly.toLocaleString()
                        : feature.monthly || "N/A"}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="py-24 bg-bg-50 relative z-0">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Two-column layout: left header, right individual plan card */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* Left: Section header and subtitle */}
            <div>
              <div className="my-8">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                  <span className="text-fg-60">Flexible pricing for</span>{" "}
                  <span className="text-fg-40">every</span>
                  <br />
                  <span className="text-fg-40">need</span>
                </h2>
                <p className="text-fg-60 text-lg">
                  Leeb AI generates high-converting proposals with AI, helping
                  you close more deals without wasting time.
                </p>
              </div>
            </div>

            {/* Right: Individual pro plan card only */}
            <div>
              {individualPlan && (
                <PlanCard
                  plan={individualPlan}
                  planType="Individual"
                  blobColor="bg-blue-500"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlansSection;
